import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetServiceQuery,
  useUpdateServiceMutation,
  useGetServiceCategoriesQuery,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import ImageUpload from "../components/ImageUpload";
import Icon from "../components/Icon";
import RichTextEditor from "../components/RichTextEditor";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";

const EditService = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();

  // RTK Query hooks
  const { data: serviceData, isLoading: loading } = useGetServiceQuery(id);
  const [updateService, { isLoading: saving }] = useUpdateServiceMutation();
  const { data: categoriesData, isLoading: loadingCategories } =
    useGetServiceCategoriesQuery();

  const categories = categoriesData?.categories || [];
  const [saveStatus, setSaveStatus] = useState("");
  const messageType = useMessageType(saveStatus);

  const [formData, setFormData] = useState({
    title: "",
    titleAr: "",
    content: "",
    contentAr: "",
    image: null,
    category_id: "",
    is_active: true,
    keywords: "",
  });

  const [originalService, setOriginalService] = useState(null);

  // Populate form when service data is loaded
  useEffect(() => {
    if (serviceData && serviceData.service) {
      const service = serviceData.service;
      setOriginalService(service);
      setFormData({
        title: service.title || "",
        titleAr: service.title_ar || "",
        content: service.content || "",
        contentAr: service.content_ar || "",
        image: service.image_url || null,
        category_id: service.category_id || "",
        is_active: service.is_active !== false,
        keywords: service.keywords || "",
      });
    }
  }, [serviceData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      setSaveStatus(t("addEditService.pleaseLoginToSave"));
      return;
    }

    // Validate form
    if (!formData.title.trim()) {
      setSaveStatus(t("addEditService.pleaseEnterTitle"));
      return;
    }

    if (!formData.content.trim()) {
      setSaveStatus(t("addEditService.pleaseEnterContent"));
      return;
    }

    if (!formData.category_id) {
      setSaveStatus(t("addEditService.pleaseSelectCategory"));
      return;
    }

    setSaveStatus("");

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("titleAr", formData.titleAr);
      submitData.append("content", formData.content);
      submitData.append("contentAr", formData.contentAr);
      submitData.append("category_id", formData.category_id);
      submitData.append("is_active", formData.is_active ? "1" : "0");
      submitData.append("keywords", formData.keywords || "");

      // Only append image if it's a new file (not existing URL)
      if (formData.image && typeof formData.image === "object") {
        submitData.append("image", formData.image);
      }

      const result = await updateService({ id, body: submitData }).unwrap();

      if (result.success) {
        setSaveStatus(t("addEditService.serviceUpdatedSuccessfully"));
        setTimeout(() => {
          navigate("/dashboard/services");
        }, 1500);
      } else {
        setSaveStatus(t("addEditService.errorUpdatingService"));
      }
    } catch (error) {
      console.error("Error updating service:", error);
      setSaveStatus(
        error.data?.message || t("addEditService.errorUpdatingService")
      );
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/services");
  };

  if (loading) {
    return (
      <div className="add-service">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("addEditService.loadingService")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="add-service">
        <div className="auth-required">
          <h2>{t("addEditService.authenticationRequired")}</h2>
          <p>{t("addEditService.pleaseLoginToEdit")}</p>
          <button className="login-btn" onClick={() => navigate("/login")}>
            {t("addEditService.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  if (!originalService) {
    return (
      <div className="add-service">
        <div className="auth-required">
          <h2>{t("addEditService.serviceNotFound")}</h2>
          <p>{t("addEditService.serviceNotFoundMessage")}</p>
          <button
            className="login-btn"
            onClick={() => navigate("/dashboard/services")}
          >
            {t("addEditService.backToServices")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="add-service">
      <div className="page-header">
        <div className="header-content">
          <button type="button" className="back-btn" onClick={handleCancel}>
            <Icon name="arrow-left" size="16px" />
            {t("addEditService.backToServices")}
          </button>
          <h1>{t("addEditService.editService")}</h1>
          <p>{t("addEditService.updateServiceDetails")}</p>
        </div>
      </div>

      <div className="form-container">
        <div className="form-card">
          <div className="form-content">
            <div className="form-group">
              <label>{t("addEditService.serviceTitleEnglish")} *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder={t("addEditService.enterServiceTitle")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceTitleArabic")}</label>
              <input
                type="text"
                value={formData.titleAr}
                onChange={(e) => handleInputChange("titleAr", e.target.value)}
                placeholder={t("addEditService.enterServiceTitleArabic")}
                className="form-input"
                dir="rtl"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceCategory")} *</label>
              <select
                value={formData.category_id}
                onChange={(e) =>
                  handleInputChange("category_id", e.target.value)
                }
                className="form-input"
                disabled={loadingCategories}
              >
                <option value="">
                  {loadingCategories
                    ? t("servicesDashboard.loading")
                    : t("addEditService.selectCategory")}
                </option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <ImageUpload
                label={t("addEditService.serviceImage")}
                value={formData.image}
                onChange={(file) => handleInputChange("image", file)}
                className="image-upload-modern"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceContentEnglish")} *</label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => handleInputChange("content", content)}
                placeholder={t("addEditService.enterServiceDetails")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceContentArabic")}</label>
              <RichTextEditor
                value={formData.contentAr}
                onChange={(content) => handleInputChange("contentAr", content)}
                placeholder={t("addEditService.enterServiceDetailsArabic")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.keywords")}</label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => handleInputChange("keywords", e.target.value)}
                placeholder={t("addEditService.enterKeywords")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditService.keywordsHint")}
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange("is_active", e.target.checked)
                  }
                />
                <span>{t("addEditService.activeService")}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="save-section">
        <StatusMessage
          message={saveStatus}
          type={messageType}
          show={!!saveStatus}
          autoClose={messageType === "success"}
          autoCloseDelay={3000}
          onClose={() => setSaveStatus("")}
        />
        <div className="save-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            {t("addEditService.cancel")}
          </button>
          <button
            type="button"
            className="save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="loading-spinner small"></div>
                {t("addEditService.updating")}
              </>
            ) : (
              <>
                <Icon name="save" size="16px" />
                {t("addEditService.updateService")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditService;
